import type { ComplimentaryCareEnrollment, EnrolleeRelationship } from '../../api/types';
import { relationshipLabel } from '../../components/enrollmentLabels';

/**
 * What the employee is about to change, worked out in the browser so it can be shown *before* the
 * submission rather than after.
 *
 * Deliberately measured against the version the form was opened on — the one whose values are on
 * screen — so the dialog answers "what did I just change?". HR's own summary is measured against the
 * last approved version instead, which is a different question and belongs to a different reader.
 */

export interface DraftEnrollee {
  /** The enrollee's id on the loaded version, or undefined for someone being added now. */
  id?: string;
  relationship: EnrolleeRelationship;
  firstName: string;
  lastName: string;
  age: string;
  contactPhoneNumber: string;

  /** A newly chosen file, which replaces whatever was on file. */
  fileName: string | null;

  /** What is already stored, or null once a spouse/partner switch has retired it. */
  existingDocumentFileName: string | null;
}

export interface Draft {
  hireDate: string;
  alreadyReceivingCareElsewhere: boolean;
  alreadyReceivingCareWho: string;
  alreadyReceivingCareClinic: string;

  /** One center for everyone enrolled, so it is compared once rather than per enrollee. */
  wellnessCenter: string;
  enrollees: DraftEnrollee[];
}

export interface FieldChange {
  field: string;
  from: string;
  to: string;
}

export interface EnrolleeChange {
  who: string;
  fields: FieldChange[];
}

export interface DraftChanges {
  /** Answers at the top of the form — hire date and the "already receiving care" question. */
  header: FieldChange[];
  added: string[];
  changed: EnrolleeChange[];
  removed: string[];
}

export function isEmpty(changes: DraftChanges): boolean {
  return (
    changes.header.length === 0 &&
    changes.added.length === 0 &&
    changes.changed.length === 0 &&
    changes.removed.length === 0
  );
}

export function countChanges(changes: DraftChanges): number {
  return (
    changes.header.length + changes.added.length + changes.changed.length + changes.removed.length
  );
}

const blank = '—';

function name(e: { firstName: string; lastName: string }): string {
  return `${e.firstName} ${e.lastName}`.trim();
}

/** "Yes — Jane Doe at Berwyn". Either half can be empty; see describeExistingCare for why. */
function describeCare(receiving: boolean, who: string | null, clinic: string | null): string {
  if (!receiving) {
    return 'No';
  }

  const someone = who?.trim() ?? '';
  const where = clinic?.trim() ?? '';
  const detail = someone && where ? `${someone} at ${where}` : someone || (where && `at ${where}`);

  return `Yes — ${detail || 'no detail'}`;
}

/** What the document line should read as: a newly chosen file wins over whatever was stored. */
function documentOf(e: DraftEnrollee): string {
  return e.fileName ?? e.existingDocumentFileName ?? blank;
}

function push(into: FieldChange[], field: string, from: string, to: string) {
  if (from !== to) {
    into.push({ field, from, to });
  }
}

export function describeDraftChanges(base: ComplimentaryCareEnrollment, draft: Draft): DraftChanges {
  const changes: DraftChanges = { header: [], added: [], changed: [], removed: [] };

  push(changes.header, 'Hire date', base.hireDate, draft.hireDate);
  push(
    changes.header,
    'Already receiving care',
    describeCare(
      base.alreadyReceivingCareElsewhere,
      base.alreadyReceivingCareWho,
      base.alreadyReceivingCareClinic,
    ),
    describeCare(
      draft.alreadyReceivingCareElsewhere,
      draft.alreadyReceivingCareWho,
      draft.alreadyReceivingCareClinic,
    ),
  );

  const baseCenter = base.enrollees.find((e) => e.relationship === 'teamMember')?.wellnessCenter ?? '';
  push(changes.header, 'Wellness center', baseCenter, draft.wellnessCenter.trim());

  const baseById = new Map(base.enrollees.map((e) => [e.id, e]));
  const keptIds = new Set<string>();

  for (const enrollee of draft.enrollees) {
    const was = enrollee.id ? baseById.get(enrollee.id) : undefined;

    if (!was) {
      changes.added.push(`${relationshipLabel(enrollee.relationship)}: ${name(enrollee)}`);
      continue;
    }

    keptIds.add(was.id);

    const fields: FieldChange[] = [];

    if (was.relationship !== enrollee.relationship) {
      fields.push({
        field: 'Relationship',
        from: relationshipLabel(was.relationship),
        to: relationshipLabel(enrollee.relationship),
      });
    }

    push(fields, 'Name', was.name, name(enrollee));
    push(fields, 'Age', was.age?.toString() ?? blank, enrollee.age.trim() || blank);
    push(fields, 'Contact phone', was.contactPhoneNumber ?? blank, enrollee.contactPhoneNumber.trim() || blank);
    push(fields, 'Document', was.documentFileName ?? blank, documentOf(enrollee));

    if (fields.length > 0) {
      changes.changed.push({ who: `${relationshipLabel(was.relationship)} ${was.name}`, fields });
    }
  }

  for (const was of base.enrollees) {
    if (!keptIds.has(was.id)) {
      changes.removed.push(`${relationshipLabel(was.relationship)}: ${was.name}`);
    }
  }

  return changes;
}
